import { useMemo, useState } from 'react';

import type { FullPostType } from 'src/app/pages/starterDashboardV2/shared/components';
import type { PlatformDataMap } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

import { hasValidContent } from '../utils/contentValidation';
import type { PlatformGroupedPosts, PlatformType } from '../utils/platformGrouping';
import { ALL_AVAILABLE_PLATFORMS, getFullPostType } from '../utils/platformGrouping';

type DisplayablePost = {
	fullPostType: FullPostType;
	post?: ReadyToSharePostWithPlatform; // if undefined, indicates a skeleton
};

type RecommendationsData = {
	hasFacebookGroups: boolean;
	hasRedditGroups: boolean;
	isLoading: boolean;
};

function computeAvailablePlatforms(
	validGroupedPosts: PlatformGroupedPosts,
	loadingStates: Record<PlatformType, boolean>,
): PlatformType[] {
	const platformNames = ALL_AVAILABLE_PLATFORMS.reduce<Set<PlatformType>>((acc, platformName) => {
		if (loadingStates[platformName] || validGroupedPosts[platformName].length > 0) {
			platformName === 'facebookgroups' ? acc.add('facebook') : acc.add(platformName);
		}

		return acc;
	}, new Set<PlatformType>());

	return Array.from(platformNames);
}

function groupPostsByPlatform(platformData: PlatformDataMap): PlatformGroupedPosts {
	return {
		facebook: platformData.FACEBOOK?.items || [],
		instagram: platformData.INSTAGRAM?.items || [],
		reddit: platformData.REDDIT?.items || [],
		facebookgroups: platformData.FACEBOOKGROUPS?.items || [],
	};
}

function groupLoadingStatesByPlatform(platformData: PlatformDataMap): Record<PlatformType, boolean> {
	return {
		facebook: platformData.FACEBOOK?.status === 'PENDING',
		instagram: platformData.INSTAGRAM?.status === 'PENDING',
		reddit: platformData.REDDIT?.status === 'PENDING',
		facebookgroups: platformData.FACEBOOKGROUPS?.status === 'PENDING',
	};
}

const platformFullPostTypeMapping: Record<PlatformType, FullPostType[]> = {
	facebook: ['facebook_post', 'facebook_story'],
	instagram: ['instagram_post', 'instagram_story'],
	reddit: ['reddit_group_post'],
	facebookgroups: ['facebook_group_post'],
};

const fullPostTypeWeights: Record<FullPostType, number> = {
	facebook_post: 100,
	reddit_group_post: 90,
	reddit_post: 90,
	facebook_group_post: 80,
	instagram_story: 70,
	facebook_story: 60,
	instagram_post: 50,
};

function addSkeletonsForPlatform(platform: PlatformType, posts: DisplayablePost[]) {
	platformFullPostTypeMapping[platform].forEach((fullPostType) => {
		posts.push({ fullPostType });
	});
}

function addPostsForPlatform(
	platform: PlatformType,
	validGroupedPosts: PlatformGroupedPosts,
	posts: DisplayablePost[],
) {
	validGroupedPosts[platform].forEach((post) => {
		const fullPostType = getFullPostType(platform, post.postTypeName);
		posts.push({ fullPostType, post });
	});
}

function processPlatform(
	platform: PlatformType,
	posts: DisplayablePost[],
	options: {
		validGroupedPosts: PlatformGroupedPosts;
		loadingStates: Record<PlatformType, boolean>;
		recommendationsData: RecommendationsData;
	},
) {
	const { validGroupedPosts, loadingStates, recommendationsData } = options;
	const isGroupPlatform = platform === 'facebookgroups' || platform === 'reddit';

	if (loadingStates[platform] || (isGroupPlatform && recommendationsData.isLoading)) {
		addSkeletonsForPlatform(platform, posts);
	} else {
		addPostsForPlatform(platform, validGroupedPosts, posts);
	}
}

function filterValidPlatformPosts(
	validGroupedPosts: PlatformGroupedPosts,
	selectedPlatform: PlatformType | undefined,
	availablePlatforms: PlatformType[],
	options: {
		loadingStates: Record<PlatformType, boolean>;
		recommendationsData: RecommendationsData;
	},
) {
	const { loadingStates, recommendationsData } = options;
	const posts: DisplayablePost[] = [];

	if (selectedPlatform) {
		// When Facebook is selected, include both facebook and facebookgroups
		if (selectedPlatform === 'facebook') {
			processPlatform('facebook', posts, { validGroupedPosts, loadingStates, recommendationsData });
			processPlatform('facebookgroups', posts, { validGroupedPosts, loadingStates, recommendationsData });
		} else {
			processPlatform(selectedPlatform, posts, { validGroupedPosts, loadingStates, recommendationsData });
		}

		return posts;
	}

	// If no platform is selected, return all valid posts and skeletons
	availablePlatforms.forEach((platformName) => {
		processPlatform(platformName, posts, { validGroupedPosts, loadingStates, recommendationsData });
		// Include facebookgroups posts when processing facebook platform
		if (platformName === 'facebook') {
			processPlatform('facebookgroups', posts, { validGroupedPosts, loadingStates, recommendationsData });
		}
	});

	return posts;
}

function sortFilteredPosts(validPlatformPosts: DisplayablePost[]) {
	// sort the displayable posts by fullPostType weight
	return validPlatformPosts.sort((a, b) => {
		const weightA = fullPostTypeWeights[a.fullPostType] || 0;
		const weightB = fullPostTypeWeights[b.fullPostType] || 0;
		return weightB - weightA;
	});
}

type UsePlatformFilterResult = ModelHookResult<
	{
		selectedPlatform: PlatformType | undefined;
		hasAnyValidContent: boolean;
		availablePlatforms: PlatformType[];
		displayablePosts: DisplayablePost[];
	},
	{
		selectPlatform: (platform: PlatformType) => void;
		clearPlatformSelection: () => void;
	}
>;

function validateGroupedPosts(
	groupedPosts: PlatformGroupedPosts,
	loadingStates: Record<PlatformType, boolean>,
	recommendationsData: RecommendationsData,
) {
	return ALL_AVAILABLE_PLATFORMS.reduce<PlatformGroupedPosts>(
		(acc, platformName) => {
			if (loadingStates[platformName]) return acc;

			const posts = groupedPosts[platformName];

			const validPosts = posts.filter((post) => {
				return hasValidContent(post, post.postTypeName.toLowerCase() === 'story', recommendationsData);
			});

			return {
				...acc,
				[platformName]: validPosts,
			};
		},
		{
			facebook: [],
			instagram: [],
			reddit: [],
			facebookgroups: [],
		},
	);
}

export function usePlatformFilter(
	platformData: PlatformDataMap,
	recommendationsData: RecommendationsData,
): UsePlatformFilterResult {
	// State management for selected platform (null means show all platforms)
	const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | undefined>(undefined);

	const groupedPosts: PlatformGroupedPosts = useMemo(() => {
		return groupPostsByPlatform(platformData);
	}, [platformData]);

	// Transform loading states from context
	const loadingStates: Record<PlatformType, boolean> = useMemo(() => {
		return groupLoadingStatesByPlatform(platformData);
	}, [platformData]);

	const selectPlatform = (platform: PlatformType) => {
		setSelectedPlatform(platform);
	};

	const clearPlatformSelection = () => {
		setSelectedPlatform(undefined);
	};

	const validGroupedPosts = useMemo(() => {
		return validateGroupedPosts(groupedPosts, loadingStates, recommendationsData);
	}, [groupedPosts, loadingStates, recommendationsData]);

	// Compute available platforms from ALL valid grouped posts (not filtered by selection)
	const availablePlatforms = useMemo(() => {
		return computeAvailablePlatforms(validGroupedPosts, loadingStates);
	}, [loadingStates, validGroupedPosts]);

	const filteredValidPlatformPosts = useMemo((): DisplayablePost[] => {
		return filterValidPlatformPosts(validGroupedPosts, selectedPlatform, availablePlatforms, {
			loadingStates,
			recommendationsData,
		});
	}, [validGroupedPosts, selectedPlatform, availablePlatforms, loadingStates, recommendationsData]);

	const displayablePosts = useMemo(() => {
		return sortFilteredPosts(filteredValidPlatformPosts);
	}, [filteredValidPlatformPosts]);

	// whether a platform is still loading or already has valid content
	const hasAnyValidContent = availablePlatforms.length > 0;

	return {
		data: {
			selectedPlatform,
			hasAnyValidContent,
			availablePlatforms,
			displayablePosts,
		},
		actions: {
			selectPlatform,
			clearPlatformSelection,
		},
	};
}
